/**
 * Learning Module Types
 */

export interface Course {
  id: string;
  title: string;
  slug: string;
  description: string;
  thumbnail_url?: string;
  cover_url?: string;
  duration_hours?: number;
  certificate_available?: boolean;
  is_enrolled?: boolean;
  module_count?: number;
  lesson_count?: number;
  progress_percentage?: number;
  enrolled_at?: string;
  last_accessed_at?: string;
  completed_at?: string;
}

export interface CourseDetail extends Course {
  enrollment_id?: string;
  total_lessons?: number;
  completed_lessons?: number;
  modules: CourseModule[];
}

export interface CourseModule {
  id: string;
  title: string;
  description?: string;
  order: number;
  lesson_count: number;
  lessons: Lesson[];
}

export interface Lesson {
  id: string;
  module_id?: string;
  title: string;
  description?: string;
  order: number;
  video_url?: string;
  video_duration?: number;
  content_html?: string;
  materials?: Material[];
  is_free: boolean;
  is_completed?: boolean;
  progress_percentage?: number;
  last_position?: number;
}

export interface Material {
  id?: string;
  lesson_id?: string;
  lesson_title?: string;
  title: string;
  type: 'pdf' | 'link' | 'image' | 'video' | 'document';
  url: string;
  size?: string;
  description?: string;
}

/** Posição do artigo na vitrine da home do Blog (definida no Admin). */
export type BlogHighlight = 'featured' | 'recommended';

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt?: string;
  content_html?: string;
  category?: string | null;
  category_slug?: string | null;
  read_time_minutes?: number | null;
  highlight?: BlogHighlight | null;
  course_title?: string;
  course_id?: string;
  thumbnail_url?: string;
  cover_image_url?: string | null;
  /** object-position da capa ("50% 30%"); ausente = centralizada. */
  cover_position?: string | null;
  video_url?: string;
  video_duration?: number;
  materials?: Material[];
  created_at?: string;
  updated_at?: string;
}

/** Vitrine do Blog: destaque principal + faixa de recomendados. */
export interface BlogHighlightsResponse {
  featured: BlogPost | null;
  recommended: BlogPost[];
}

export interface BlogCategoryOption {
  name: string;
  slug: string;
}

export interface Video {
  id: string;
  title: string;
  description?: string;
  thumbnail_url?: string | null;
  cover_position?: string | null;
  duration_seconds?: number | null;
  created_at?: string;
}

export interface VideoDetail extends Video {
  video_url: string;
}

/**
 * Links assinados de uma vídeo-aula. O arquivo não é público: o backend emite
 * URLs de curta duração, uma para tocar e outra para baixar.
 */
export interface VideoPlayback {
  stream_url: string;
  download_url: string;
  expires_at: string;
  file_name?: string | null;
  file_size?: number | null;
}

export interface VideosListResponse {
  videos: Video[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface CoursesListResponse {
  courses: Course[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface MaterialsListResponse {
  materials: Material[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface BlogPostsListResponse {
  posts: BlogPost[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Translations
export const MATERIAL_TYPE_LABELS: Record<string, string> = {
  pdf: 'PDF',
  link: 'Link',
  image: 'Imagem',
  video: 'Vídeo',
  document: 'Documento',
};

export const MATERIAL_TYPE_ICONS: Record<string, string> = {
  pdf: 'FileText',
  link: 'ExternalLink',
  image: 'Image',
  video: 'Play',
  document: 'File',
};

